import { useState, useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import DisclaimerModal from './DisclaimerModal';
import Footer from '@/components/Footer';
import OnboardingTour from './OnboardingTour';
import FloatingChatWidget from './FloatingChatWidget';
import { useAuth } from '@/hooks/useAuth';
import ParticleBackground from '@/components/ParticleBackground';
import GradientBackground from '@/components/GradientBackground';

const DashboardLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const { user, loading } = useAuth();

  useEffect(() => {
    // Check if user has accepted disclaimer
    const hasAccepted = localStorage.getItem('tam_disclaimer_accepted');
    if (user && !hasAccepted) {
      setShowDisclaimer(true);
    }
  }, [user]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const handleDisclaimerAccept = () => {
    localStorage.setItem('tam_disclaimer_accepted', 'true');
    setShowDisclaimer(false);
  };

  const handleMenuToggle = () => {
    // On mobile, toggle mobile menu
    if (window.innerWidth < 1024) {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <GradientBackground />
      <ParticleBackground />
      
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      <Sidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
      />
      
      <DashboardHeader 
        onMenuToggle={handleMenuToggle}
        isSidebarCollapsed={isSidebarCollapsed}
      />
      
      <main 
        className={cn(
          "pt-16 flex-1 transition-all duration-300",
          isSidebarCollapsed ? "lg:pl-16" : "lg:pl-64",
          "pl-0" // No padding on mobile
        )}
      >
        <div className="p-4 sm:p-6 min-h-[calc(100vh-8rem)]">
          <Outlet />
        </div>
      </main>

      <div className={cn(
        "transition-all duration-300",
        isSidebarCollapsed ? "lg:pl-16" : "lg:pl-64",
        "pl-0"
      )}>
        <Footer />
      </div>

      <DisclaimerModal open={showDisclaimer} onAccept={handleDisclaimerAccept} />
      <OnboardingTour />
      <FloatingChatWidget />
    </div>
  );
};

export default DashboardLayout;
